import { Box, Grid, Typography, Alert } from '@mui/material';
import { useMemo, useState } from 'react';
import { BulkDownloadTable, Link } from '../../../components';
import { CodeBlock } from '../code-block';
import { handleCopyClick } from '../constants';
import { StyleGuidePage } from '../style-guide-page';
import { IDropdownResponse } from '../../../models';
import { useQuery } from 'react-query';
import { getBulkDownloadTestData } from '../../../fetch';
import { downloadTestFile } from '../../../helpers';

export const StyleGuideBulkDownloadGrid = () => {
  const [isCopied, setIsCopied] = useState<{
    [val: string]: boolean;
  }>({});
  const [selectedDocs, setSelectedDocs] = useState<IDropdownResponse[]>([]);

  const { isLoading, data: docData } = useQuery<IDropdownResponse[], Error>(['doc-data'], () =>
    getBulkDownloadTestData()
  );
  const testDocs = useMemo(() => docData ?? [], [docData]);

  return (
    <StyleGuidePage title="Bulk Download Grid">
      <Grid container mb={2}>
        <Grid item xs={12} lg={8}>
          <Typography mb={1}>
            The bulk download grid component was built to render a table of downloadable files with
            the option of selecting multiples and downloading a .zip file. The component has
            built-in styles, pagination, and multi-select functionality. It is, however, expected to
            wire-up the component to an endpoint that zips multiple selected files, as that
            functionality has thus far been implemented on the backend.
          </Typography>
          <Typography mb={1}>
            This component is used on pages like{' '}
            <Link to="/sales/sales-tools/marketing">Marketing Materials</Link>, where the user
            generates PDFs available for download. This component provides the user with the option
            of downloading more than one file and compiling their own .zip files for bulk download.
          </Typography>
          <Box mb={1}>
            <Alert severity="info">
              NOTE: The example below is wired-up up to test data and won't necessarily reflect a
              final implmentation, as data should be coming from the API, not static files. Example:
              The .zip file is static in the example below, not dynamic based on selection, as that
              logic will normally be implemented on the backend.
            </Alert>
          </Box>
          <Typography mb={1}>
            <code>
              Props:
              <br />- data: IDropdownResponse[]
              <br />- downloadApiRequest?: (docs: any, filters?: any) =&gt; Promise&lt;any&gt;
              <br />- downloadApiParams?: any
              <br />- isLoading?: boolean
              <br />- selectedDocs: IDropdownResponse[]
              <br />- setSelectedDocs:
              React.Dispatch&lt;React.SetStateAction&lt;ISampleContractDocuments[]&gt;&gt;
              <br />- bundleLabel?: string
              <br />- transformFile?: (label?: string) =&gt; Promise&lt;any&gt;
              <br />- bulkTransformFile?: (docs: any, label?: string) =&gt; Promise&lt;any&gt;
            </code>
          </Typography>
          <Typography mb={1}>
            <code>
              Common variables:
              <br />- selectedDocs: (useState) IDropdownResponse[]
              <br />- fetch/GET: (useQuery) Api get call with isLoading for loading state
              <br />- documents: (useMemo) data from useQuery
            </code>
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box mb={1}>
            {testDocs && testDocs.length !== 0 && (
              <BulkDownloadTable
                data={testDocs}
                bundleLabel="sample-documents.zip"
                bulkTransformFile={(docs, defaultLabel) =>
                  downloadTestFile('/data/test-pdf-zip.zip', defaultLabel)
                }
                transformFile={docs => downloadTestFile(`/data/${docs}`, docs)}
                isLoading={isLoading}
                selectedDocs={selectedDocs}
                setSelectedDocs={setSelectedDocs}
              />
            )}
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;BulkDownloadTable <br />
            &nbsp;&nbsp;&nbsp;data=&#123;testDocData&#125; <br />
            &nbsp;&nbsp;&nbsp;downloadApiParams=&#123;&#123;filter1: 'something'&#125;&#125; <br />
            &nbsp;&nbsp;&nbsp;downloadApiRequest=&#123;(docs, filters) =&gt; somePOSTCall(docs,
            filters) &#125; <br />
            &nbsp;&nbsp;&nbsp;bundleLabel="sample-documents.zip" <br />
            &nbsp;&nbsp;&nbsp;isLoading=&#123;isLoading&#125; <br />
            &nbsp;&nbsp;&nbsp;selectedDocs=&#123;selectedDocs&#125; <br />
            &nbsp;&nbsp;&nbsp;setSelectedDocs=&#123;setSelectedDocs&#125; <br />
            /&gt;
          </CodeBlock>
        </Grid>
      </Grid>
    </StyleGuidePage>
  );
};
